import React, { useState } from 'react';
import { renderField } from './DynamicForm';

/**
 * ArrayOfObjectsField - Handles arrays of embedded documents
 * Example: accounts: [{ account: 'checking', balance: 100 }, ...]
 */
export const ArrayOfObjectsField = ({ field, value, onChange, error }) => {
  const [expandedItems, setExpandedItems] = useState(new Set([0])); // First item expanded by default

  const items = value || [];

  const handleItemChange = (index, newItemValue) => {
    const newArray = [...items];
    newArray[index] = newItemValue;
    onChange(newArray);
  };

  const handleAddItem = () => {
    const newItem = {};
    // Initialize with default values
    if (field.itemSchema?.fields) {
      Object.entries(field.itemSchema.fields).forEach(([fieldName, fieldDef]) => {
        if (fieldDef.default !== undefined) {
          newItem[fieldName] = fieldDef.default;
        }
      });
    }
    onChange([...items, newItem]);
    // Expand the new item
    setExpandedItems(new Set([...expandedItems, items.length]));
  };

  const handleRemoveItem = (index) => {
    if (window.confirm('Are you sure you want to remove this item?')) {
      const newArray = items.filter((_, i) => i !== index);
      onChange(newArray);
      // Remove from expanded set
      const newExpanded = new Set(expandedItems);
      newExpanded.delete(index);
      setExpandedItems(newExpanded);
    }
  };

  const handleToggleExpand = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const handleFieldChange = (itemIndex, fieldName, fieldValue) => {
    const newItem = { ...items[itemIndex], [fieldName]: fieldValue };
    // Preserve _id if it exists
    if (items[itemIndex]._id) {
      newItem._id = items[itemIndex]._id;
    }
    handleItemChange(itemIndex, newItem);
  };

  // Get a display label for an item (for the collapsed view)
  const getItemLabel = (item, index) => {
    if (!item) return `Item ${index + 1}`;
    
    // Try to find a meaningful field to display
    const labelFields = ['name', 'title', 'account', 'label', 'type'];
    for (const fieldName of labelFields) {
      if (item[fieldName]) {
        return `${item[fieldName]}`;
      }
    }
    
    return `Item ${index + 1}`;
  };

  return (
    <div className="array-of-objects-field">
      <div className="array-field-header">
        <label>{field.label}</label>
        <button
          type="button"
          onClick={handleAddItem}
          className="btn btn-sm btn-primary"
        >
          + Add {field.itemLabel || 'Item'}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="array-empty-state">
          No {field.label?.toLowerCase() || 'items'} yet. Click "Add" to create one.
        </div>
      ) : (
        <div className="array-items">
          {items.map((item, index) => {
            const isExpanded = expandedItems.has(index);
            
            return (
              <div key={index} className={`array-item ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="array-item-header">
                  <button
                    type="button"
                    onClick={() => handleToggleExpand(index)}
                    className="expand-toggle"
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>
                  <span className="array-item-label">
                    {getItemLabel(item, index)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className="btn btn-sm btn-danger"
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>

                {isExpanded && field.itemSchema?.fields && (
                  <div className="array-item-content">
                    {Object.entries(field.itemSchema.fields).map(([fieldName, fieldDef]) => (
                      <div key={fieldName} className="array-item-field">
                        {renderField(
                          fieldName,
                          fieldDef,
                          item[fieldName],
                          (val) => handleFieldChange(index, fieldName, val),
                          item,
                          1 // depth for nested rendering
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && <span className="error-message">{error}</span>}
    </div>
  );
};

export default ArrayOfObjectsField;
